import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowUp, Mail, Phone, MapPin,
  Linkedin, Twitter, Facebook, Globe
} from 'lucide-react';

export default function Footer() {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const checkScrollHeight = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', checkScrollHeight);
    return () => window.removeEventListener('scroll', checkScrollHeight);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <footer className="relative z-10 bg-transparent border-t border-white/5 pt-20 pb-10 overflow-hidden">

      {/* Subtle bottom glows */}
      <div className="absolute bottom-0 left-[20%] w-[35vw] h-[35vw] rounded-full bg-brand-cyan/2 blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 right-[20%] w-[35vw] h-[35vw] rounded-full bg-brand-blue/2 blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8 mb-16 text-left">

          {/* Brand Info */}
          <div className="lg:col-span-4 flex flex-col items-start">
            <Link to="/" className="flex items-center gap-2 group">
              {/* <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-brand-blue to-brand-cyan flex items-center justify-center shadow-md shadow-brand-cyan/20 group-hover:scale-105 transition-transform duration-300">
                          <span className="font-display font-extrabold text-white text-lg tracking-wider">A</span>
                        </div> */}
              <div className="flex flex-col">
                <span className="font-display font-bold text-gradient tracking-wide text-2xl leading-tight group-hover:text-brand-cyan transition-colors duration-300">
                  ADVANCE IT
                </span>
                <span className="font-sans text-[10px] text-slate-400 tracking-[0.2em] font-medium leading-none">
                  SERVICES
                </span>
              </div>
            </Link>


            <p className="font-sans text-xs text-slate-400 leading-relaxed mb-6 max-w-sm mt-6">
              Established in 1999, Advance IT Services is a trusted system integrator delivering enterprise ICT networking, cloud migration workflows, secure servers, and critical cybersecurity architectures.
            </p>

            {/* Social Icons */}
            {/* <div className="flex gap-3">
              {[
                { icon: Linkedin, link: 'https://linkedin.com' },
                { icon: Twitter, link: 'https://twitter.com' },
                { icon: Facebook, link: 'https://facebook.com' },
                { icon: Globe, link: 'https://advanceitservices.in' }
              ].map((social, i) => (
                <a
                  key={i}
                  href={social.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 hover:border-brand-cyan/40 hover:bg-brand-cyan/10 text-slate-400 hover:text-brand-cyan flex items-center justify-center transition-all"
                >
                  <social.icon size={16} />
                </a>
              ))}
            </div> */}
          </div>

          {/* Quick Sitemap */}
          <div className="lg:col-span-2 lg:col-start-6">
            <h4 className="font-display font-bold text-xs text-white uppercase tracking-wider mb-6">
              Site Navigation
            </h4>
            <div className="flex flex-col gap-3">
              {[
                { name: 'Home', path: '/' },
                { name: 'Services', path: '/services' },
                { name: 'About Us', path: '/about' },
                { name: 'FAQs', path: '/faqs' },
                { name: 'Contact', path: '/contact' }
              ].map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className="font-sans text-xs text-slate-400 hover:text-brand-cyan transition-colors"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Services Links */}
          <div className="lg:col-span-3">
            <h4 className="font-display font-bold text-xs text-white uppercase tracking-wider mb-6">
              IT Architectures
            </h4>
            <div className="flex flex-col gap-3">
              {[
                'Server Solutions',
                'Enterprise Networking',
                'Cloud Migrations',
                'CCTV Surveillance Systems',
                'Laptop & Desktop Rentals',
                'Security Solutions'
              ].map((service) => (
                <Link
                  key={service}
                  to="/services"
                  className="font-sans text-xs text-slate-400 hover:text-brand-cyan transition-colors"
                >
                  {service}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact Direct Info */}
          <div className="lg:col-span-3">
            <h4 className="font-display font-bold text-xs text-white uppercase tracking-wider mb-6">
              Contact Details
            </h4>
            <div className="flex flex-col gap-4 text-xs text-slate-400">
              <div className="flex items-start gap-2.5">
                <MapPin size={14} className="text-brand-cyan shrink-0 mt-0.5" />
                <span className="leading-relaxed">
                  4th Floor, 401- 402, Meghansh Complex, Gurukul Rd, opposite Oxford Tower, Memnagar, Ahmedabad, Gujarat 380052
                </span>
              </div>
              <div className="flex items-center gap-2.5">
                <Phone size={14} className="text-brand-cyan shrink-0" />
                <a href="tel:+919824464400" className="hover:text-brand-cyan transition-colors">
                  +91 98244 64400
                </a>
              </div>
              <div className="flex items-center gap-2.5">
                <Mail size={14} className="text-brand-cyan shrink-0" />
                <a href="mailto:info@advanceitservices.in" className="hover:text-brand-cyan transition-colors">
                  info@advanceitservices.in
                </a>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom copyright segment */}
        <div className="border-t border-white/5 pt-10 flex flex-col md:flex-row justify-between items-center gap-4 text-[10px] text-slate-500 font-mono">
          <span>&copy; {new Date().getFullYear()} advanceits. ALL RIGHTS RESERVED. Developed By 
            <a
            className='underline text-orange-500 font-bold ml-1.5'
              href="https://www.tentoseven.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              TenToSeven
            </a>
          </span>
          <div className="flex gap-6">
            <span>ESTD. 1999</span>
          </div>
        </div>
      </div>

      {/* Floating Scroll Top Trigger */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 z-50 p-3.5 rounded-xl bg-gradient-to-r from-brand-cyan to-brand-blue hover:from-brand-blue hover:to-brand-cyan text-white shadow-lg shadow-brand-cyan/20 border border-brand-cyan/20 hover:scale-105 hover:shadow-brand-cyan/35 transition-all cursor-pointer"
            aria-label="Scroll to top"
          >
            <ArrowUp size={16} />
          </motion.button>
        )}
      </AnimatePresence>

    </footer>
  );
}
